import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { useLanguage } from '../../features/language/useLanguage';

interface RoastStage {
  time: number;
  temperature: number;
  name: string;
}

interface RoastProfileChartProps {
  data: RoastStage[];
}

export const RoastProfileChart: React.FC<RoastProfileChartProps> = ({ data }) => {
  const { t } = useLanguage();
  // Sort data by time to ensure correct line rendering and filter out invalid values
  // Create a deep copy of items to prevent mutation of the original data by Recharts
  const sortedData = (data || [])
    .map(item => ({ ...item }))
    .filter(item => 
      item.time !== undefined && 
      item.temperature !== undefined && 
      !isNaN(Number(item.time)) && 
      !isNaN(Number(item.temperature))
    )
    .sort((a, b) => a.time - b.time);

  if (sortedData.length === 0) {
    return (
      <div className="h-64 w-full bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex items-center justify-center text-gray-400 text-sm">
        {t('recipes.roastProfileChart.addStagesToView')}
      </div>
    );
  }

  return (
    <div className="h-64 w-full bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-test-id="roast-profile-chart">
      <h3 className="text-sm font-medium text-gray-700 mb-4">{t('recipes.roastProfileChart.visualization')}</h3>
      <div className="w-full h-[calc(100%-2rem)]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={sortedData}
            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="time" 
              type="number" 
              domain={['dataMin', 'dataMax']} 
              unit=" min"
              stroke="#6b7280"
              tick={{ fontSize: 12 }}
              allowDecimals={true}
            />
            <YAxis 
              domain={['auto', 'auto']} 
              unit="°C"
              stroke="#6b7280"
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            />
            <Line
              type="monotone"
              dataKey="temperature"
              stroke="#d97706" // amber-600
              strokeWidth={2}
              dot={{ r: 4, fill: '#d97706', strokeWidth: 2 }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            >
              <LabelList dataKey="name" position="top" offset={10} fontSize={10} />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
